/**
 * The one way this app hands a browser to the Vue 3 app on the same hostname. Two callers: the
 * "Use New App (Beta)" switch in Navigation.vue, and the default-on push for opted-in
 * enterprises' staff in main.js (see vue3DefaultOn.js).
 *
 * Sets the ff_ui cookie and hard-navigates to the Vue 3 spelling of the given route on THIS
 * origin — the root `middleware.js` (Vercel Routing Middleware) then proxies the request to the
 * Vue 3 deployment, and the Firebase session carries over because the origin is unchanged.
 *
 * Retiring this app's service worker first is not optional: its Workbox precache serves `/`
 * from CacheStorage, so the request would never leave the browser and the proxy would never
 * see it — the user would be switched and still land on the legacy shell. The teardown
 * swallows its own failures so a browser that refuses still gets navigated.
 */
import { setVue3Cookie } from '@/utils/uiVersionCookie'
import { unregisterLegacyServiceWorker } from '@/utils/legacyAppTeardown'
import { toNewAppPath } from '@/utils/newAppUrl'

/**
 * A path on this origin: one leading `/`, not followed by `/` or `\` (browsers read both
 * `//host` and `/\host` as another site). Anything else lands on the root.
 */
function sameOriginPath (target) {
  return /^\/(?![/\\])/.test(target) ? target : '/'
}

/**
 * @param {string} fullPath the current route's fullPath
 * @param {object} [deps] browser seams, injectable for tests
 */
export async function switchToNewApp (fullPath, {
  setCookie = setVue3Cookie,
  teardown = unregisterLegacyServiceWorker,
  assign = url => window.location.assign(url),
} = {}) {
  setCookie()
  await teardown()
  assign(sameOriginPath(toNewAppPath(fullPath)))
}
